import { useNavigate, Navigate } from 'react-router-dom';
import { usePartnerAuth } from '../../context/PartnerAuthContext';

export default function PartnerDashboard() {
  const { partnerSession, partnerProfile, loadingAuth, signOut } = usePartnerAuth();
  const navigate = useNavigate();

  // While the initial session check runs, show a minimal loading state so we
  // don't flash a redirect for a returning, already-logged-in partner.
  if (loadingAuth) {
    return (
      <div className="w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter">
        <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">
          <p className="font-dm-sans text-sm text-white/55 font-light leading-relaxed text-center">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  // Not logged in → send to the partner login screen.
  if (!partnerSession) return <Navigate to="/partner/login" replace />;

  const handleLogout = async () => {
    await signOut();
    navigate('/partner/login');
  };

  const displayName = partnerProfile?.name || partnerSession?.user?.email || 'Partner';
  const isSuspended = partnerProfile?.approval_status === 'suspended';
  const isRejected = partnerProfile?.approval_status === 'rejected';
  const isPending = partnerProfile?.approval_status === 'pending';
  // A login with NO partners row (a staff account, or a partner whose record
  // was unlinked). Every booking screen guards on approval_status === 'approved',
  // so offering Book a Cruise here would only bounce straight back; say so.
  const isUnlinked = !partnerProfile;

  return (
    <div className="w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter">
      <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">

        <p className="font-dm-sans text-[12px] uppercase tracking-[0.18em] text-[rgba(201,168,76,0.85)] mb-2">
          Partner Portal
        </p>

        {isSuspended ? (
          <>
            <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-1.5">
              Account suspended
            </h1>
            <p className="font-dm-sans text-sm text-white/55 font-light mt-3 leading-relaxed">
              Your partner account has been suspended. Please contact Alfresco Grand at 9854958549 / 9435558001 for assistance.
            </p>
          </>
        ) : isRejected ? (
          <>
            <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-1.5">
              Application not approved
            </h1>
            <p className="font-dm-sans text-sm text-white/55 font-light mt-3 leading-relaxed">
              Your partner application was not approved. Please contact Alfresco Grand at 9854958549 / 9435558001 if you have any questions.
            </p>
          </>
        ) : isPending ? (
          <>
            <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-1.5">
              Welcome, {displayName}
            </h1>
            <p className="font-dm-sans text-sm text-white/55 font-light mt-3 leading-relaxed">
              Your account is under review. We'll notify you by email once it has been approved.
            </p>
          </>
        ) : isUnlinked ? (
          <>
            <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-1.5">
              No partner account linked
            </h1>
            <p className="font-dm-sans text-sm text-white/55 font-light mt-3 leading-relaxed">
              {displayName} is signed in, but no partner account is linked to this login, so bookings can't be made from it. If you are a travel partner, please contact Alfresco Grand at 9854958549 / 9435558001.
            </p>
          </>
        ) : (
          <>
            <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-1.5">
              Welcome, {displayName}
            </h1>
            <p className="font-dm-sans text-sm text-white/55 font-light mt-3 leading-relaxed">
              You're signed in to the partner portal.
            </p>

            {/* Approved partners only — enter the partner self-service booking flow. */}
            <button
              type="button"
              onClick={() => navigate('/partner/book/agent')}
              className="w-full mt-8 py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-medium text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.97] hover:bg-[#B8973E] transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]"
            >
              Book a Cruise
            </button>

            {/* Approved partners only — view their own booking history. */}
            <button
              type="button"
              onClick={() => navigate('/partner/bookings')}
              className="w-full mt-3 py-[14px] border border-[rgba(212,168,67,0.4)] text-[#D4A843] font-dm-sans font-medium text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.97] hover:border-[rgba(212,168,67,0.7)] hover:bg-[rgba(212,168,67,0.06)] transition-[transform,border-color,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]"
            >
              My Bookings
            </button>

            {/* Approved partners only — self-serve profile (contact/GST/agents). */}
            <button
              type="button"
              onClick={() => navigate('/partner/profile')}
              className="w-full mt-3 py-[14px] border border-[rgba(212,168,67,0.4)] text-[#D4A843] font-dm-sans font-medium text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.97] hover:border-[rgba(212,168,67,0.7)] hover:bg-[rgba(212,168,67,0.06)] transition-[transform,border-color,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]"
            >
              My Profile
            </button>
          </>
        )}

        <button
          type="button"
          onClick={handleLogout}
          className="w-full mt-8 py-[14px] border border-[rgba(212,168,67,0.4)] text-[#D4A843] font-dm-sans font-medium text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.97] hover:border-[rgba(212,168,67,0.7)] hover:bg-[rgba(212,168,67,0.06)] transition-[transform,border-color,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]"
        >
          Log out
        </button>

      </div>
    </div>
  );
}
